import { motion } from 'framer-motion';
import { ArrowUpRight, Linkedin, Instagram } from 'lucide-react';
import { PORTFOLIO } from '@/constants/testIds';
import Testimonials from './Testimonials';

// Editorial contact spread. No outer card, no nested boxes — just
// a top hairline, a two-column split with a vertical hairline
// divider between the "Hi everyone" wordmark and the pull-quote,
// followed by two inline social links and the giant HIMANSHU
// outline text. Reads like a magazine masthead.

const socials = [
    {
        name: 'LinkedIn',
        href: 'https://www.linkedin.com/in/anshuu/',
        handle: '/in/anshuu',
        testId: PORTFOLIO.contactSocialLinkedin,
        Icon: Linkedin,
    },
    {
        name: 'Instagram',
        href: 'https://www.instagram.com/anshuhim/',
        handle: '@anshuhim',
        testId: PORTFOLIO.contactSocialDribbble,
        Icon: Instagram,
    },
];

const SocialRow = ({ s }) => (
    <a
        href={s.href}
        target="_blank"
        rel="noreferrer"
        data-testid={s.testId}
        data-cursor="hover"
        className="group flex items-center justify-between gap-4 py-4 border-b border-[var(--line)] hover:border-[var(--accent)] transition-colors"
    >
        <div className="flex items-center gap-4">
            <s.Icon size={16} className="text-[var(--ink-2)] group-hover:text-[var(--accent)] transition-colors" />
            <div className="font-display-italic text-3xl md:text-4xl text-[var(--ink)] group-hover:text-[var(--accent)] transition-colors">
                {s.name}
            </div>
            <span className="hidden md:inline font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                {s.handle}
            </span>
        </div>
        <ArrowUpRight
            size={22}
            className="text-[var(--ink-3)] group-hover:text-[var(--accent)] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-300"
        />
    </a>
);

const Contact = () => {
    const year = new Date().getFullYear();
    return (
        <section
            id="contact"
            data-testid={PORTFOLIO.contactSection}
            className="relative px-4 md:px-6 pt-12 md:pt-16 pb-10"
        >
            <div className="mx-auto max-w-[1400px]">
                {/* Top eyebrow row — hairline + label + year clock */}
                <div className="flex items-center gap-4 pb-6 md:pb-10 border-b border-[var(--line)]">
                    <span className="inline-flex items-center gap-2 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
                        Say hello
                    </span>
                    <span className="flex-1 h-[1px] bg-[var(--line)]" />
                    <span className="font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                        Available for lead / principal roles
                    </span>
                </div>

                {/* Editorial two-column spread */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0 pt-10 md:pt-14">
                    {/* Left — headline + socials */}
                    <div className="lg:col-span-7 lg:pr-14 xl:pr-20">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9 }}
                            className="font-display leading-[0.88] text-[16vw] md:text-[10.5vw] lg:text-[7.6vw] text-[var(--ink)]"
                        >
                            Hi{' '}
                            <span className="font-display-italic text-[var(--accent)]">
                                everyone
                            </span>
                            <span className="text-[var(--accent)]">!</span>
                        </motion.h2>
                        <p className="mt-6 max-w-md font-sans-brand text-[15px] md:text-[16px] leading-[1.6] text-[var(--ink-2)]">
                            I&apos;m Himanshu, a senior product designer. The
                            quickest way to reach me is LinkedIn, or any of the
                            channels below.
                        </p>

                        <div className="mt-8 md:mt-10">
                            {socials.map((s) => (
                                <SocialRow key={s.name} s={s} />
                            ))}
                        </div>
                    </div>

                    {/* Right — editorial pull-quote (border-l acts as the divider) */}
                    <div className="lg:col-span-5 lg:pl-14 xl:pl-20 lg:border-l lg:border-[var(--line)]">
                        <Testimonials />
                    </div>
                </div>

                {/* Bottom meta row */}
                <div className="mt-10 md:mt-14 pt-6 border-t border-[var(--line)] grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-6 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                        Himanshu Kumar Tanty · Portfolio
                    </div>
                    <div
                        className="col-span-6 text-right font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]"
                        data-testid={PORTFOLIO.footerYear}
                    >
                        © {year}
                    </div>
                </div>

                {/* Giant outline wordmark */}
                <div className="mt-10 md:mt-14 select-none pointer-events-none w-screen relative left-1/2 -translate-x-1/2 overflow-hidden">
                    <div className="outline-text text-[22vw] md:text-[18vw] leading-[0.85] tracking-tighter text-center whitespace-nowrap">
                        HIMANSHU.
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
